import React from 'react';

const EnsDomain = () => {
  return (
    <section className="ens-tech-manual">
      {/* Gold Card Container */}
      <div style={{
        background: 'var(--black)',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 0 20px var(--gold)',
        maxWidth: '1000px',
        margin: '20px auto',
        fontFamily: 'Helvetica, Arial, sans-serif'
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ color: 'var(--gold)', fontSize: '32px', fontWeight: 'bold', marginBottom: '10px' }}>
            Technical Manual: ENS Domain Setup
          </h1>
          <p style={{ color: 'var(--gold)', fontSize: '16px', marginBottom: '20px' }}>
            Complete Guide to Minting .xbsys.eth Subdomains & Configuring Primary Names
          </p>
        </div>

        {/* Introduction */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            📋 Overview
          </h2>
          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <p>This technical manual provides step-by-step instructions for:</p>
            <ul style={{ paddingLeft: '20px', marginBottom: '15px' }}>
              <li>Minting your .xbsys.eth subdomain through BankSystem's custom contract</li>
              <li>Setting your domain as primary name in the ENS App</li>
              <li>Troubleshooting common issues</li>
              <li>Understanding the technical architecture</li>
            </ul>
            <p><strong>Prerequisites:</strong> MetaMask wallet, ETH for gas, basic Web3 knowledge</p>
          </div>
        </div>

        {/* Part 1: Minting Process */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            🛠️ Part 1: Minting .xbsys.eth Subdomains
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 1: Access Web3Identity</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Navigation:</strong> BankSystem → Web3Identity section</p>
              <p><strong>Contract Address:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>0x40B4d1d8bCBf4eE89005adF2c48503C53210D26C</code></p>
              <p><strong>Network:</strong> Ethereum Mainnet (Chain ID: 1)</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 2: Connect Wallet</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Action:</strong> Click "Connect Wallet" button</p>
              <p><strong>Requirements:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>MetaMask installed and unlocked</li>
                <li>Ethereum Mainnet selected</li>
                <li>Sufficient ETH for gas fees</li>
              </ul>
              <p><strong>Gas Estimate:</strong> Varies based on network conditions</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 3: Choose Subdomain</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Naming Rules:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Length:</strong> 3-63 characters</li>
                <li><strong>Characters:</strong> a-z, 0-9, hyphens (-)</li>
                <li><strong>Restrictions:</strong> Cannot start or end with hyphen</li>
                <li><strong>Examples:</strong> "alice", "john-trading", "bob2024"</li>
              </ul>
              <p><strong>Validation:</strong> Real-time checking against smart contract</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 4: Mint Transaction</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Process:</strong></p>
              <ol style={{ paddingLeft: '20px' }}>
                <li>Enter desired subdomain (e.g., "alice")</li>
                <li>Click "Mint Now" button</li>
                <li>Confirm transaction in MetaMask</li>
                <li>Wait for blockchain confirmation (~15-30 seconds)</li>
                <li>Receive success confirmation</li>
              </ol>
              <p><strong>Contract Method:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>mintSubdomain(string label)</code></p>
              <p><strong>Gas Limit:</strong> Optimized for reliability</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 5: Verification</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Confirmation:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Success alert with transaction hash</li>
                <li>Updated mint count displayed</li>
                <li>Domain immediately available for BankSystem access</li>
              </ul>
              <p><strong>View on Explorer:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>https://etherscan.io/tx/&#123;txHash&#125;</code></p>
            </div>
          </div>
        </div>

        {/* Part 2: ENS Primary Name Setup */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            ⚙️ Part 2: Setting Primary Name in ENS App
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 1: Access ENS App</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>URL:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>https://app.ens.domains</code></p>
              <p><strong>Network:</strong> Ensure you're on Ethereum Mainnet</p>
              <p><strong>Connection:</strong> Connect same wallet used for minting</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 2: Locate Your Domain</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Navigation:</strong></p>
              <ol style={{ paddingLeft: '20px' }}>
                <li>Click "My Account" in top right</li>
                <li>Select "My Names" from dropdown</li>
                <li>Find your <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>yourname.xbsys.eth</code> domain</li>
                <li>Click on the domain to enter management page</li>
              </ol>
              <p><strong>Note:</strong> It may take a few minutes for new domains to appear</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 3: Set as Primary Name</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Process:</strong></p>
              <ol style={{ paddingLeft: '20px' }}>
                <li>On your domain's management page, find "Primary ENS Name" section</li>
                <li>Click "Set Primary Name" or "Edit" button</li>
                <li>Confirm the selection in the popup</li>
                <li>Sign the transaction in MetaMask</li>
                <li>Wait for confirmation</li>
              </ol>
              <p><strong>Gas Cost:</strong> Minimal (setting reverse record)</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 4: Verification</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Confirmation:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>ENS App shows "Primary Name: yourname.xbsys.eth"</li>
                <li>Other dApps will now recognize this as your identity</li>
                <li>BankSystem automatically detects the primary name</li>
              </ul>
              <p><strong>Benefits:</strong> Human-readable address across Web3 ecosystem</p>
            </div>
          </div>
        </div>

        {/* Technical Architecture */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            🏗️ Technical Architecture
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Smart Contract Details</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Contract:</strong> Custom ENS Subdomain Registrar</p>
              <p><strong>Root Node:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>xbsys.eth</code></p>
              <p><strong>Methods:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li><code>mintSubdomain(string label)</code> - Primary minting function</li>
                <li><code>mintFee() → uint256</code> - Returns current minting fee</li>
                <li><code>mintCount(address) → uint256</code> - Tracks mints per wallet</li>
                <li><code>mintedLabels(bytes32) → bool</code> - Checks label availability</li>
              </ul>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>ENS Integration</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Registry:</strong> ENS Mainnet Registry</p>
              <p><strong>Resolver:</strong> Public Resolver for record management</p>
              <p><strong>Subdomain Structure:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Parent:</strong> xbsys.eth (owned by BankSystem)</li>
                <li><strong>Subdomain:</strong> yourname.xbsys.eth (user-owned)</li>
                <li><strong>Control:</strong> Full ownership transferred to minter</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            🐛 Troubleshooting Guide
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
              <div style={{ background: 'rgba(255,0,0,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #ff4444' }}>
                <h4 style={{ color: '#ff4444', margin: '0 0 10px 0' }}>❌ Minting Failed</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> Insufficient gas, network issues, or label taken<br/>
                  <strong>Solution:</strong> Check gas limit, ensure Mainnet, try different name
                </p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', border: '1px solid var(--gold)' }}>
                <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>🔗 Domain Not Showing</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> ENS cache delay or wrong network<br/>
                  <strong>Solution:</strong> Wait 2-5 minutes, ensure Mainnet in ENS App
                </p>
              </div>
              <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #00ff88' }}>
                <h4 style={{ color: '#00ff88', margin: '0 0 10px 0' }}>💸 Gas Too High</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> Network congestion<br/>
                  <strong>Solution:</strong> Try during off-peak hours, adjust gas limits
                </p>
              </div>
              <div style={{ background: 'rgba(0,100,255,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #0064ff' }}>
                <h4 style={{ color: '#0064ff', margin: '0 0 10px 0' }}>🔐 Primary Name Error</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> Insufficient permissions or network fee<br/>
                  <strong>Solution:</strong> Ensure wallet control, have ETH for gas
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            💡 Best Practices
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Name Selection</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Choose memorable, professional names</p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Gas Management</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Mint during low-network activity</p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Security</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Never share private keys or seed phrases</p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Verification</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Always verify transactions on Etherscan</p>
              </div>
            </div>
          </div>
        </div>

        {/* Support Information */}
        <div style={{
          background: 'var(--gold-shadow)',
          border: '1px solid var(--gold)',
          borderRadius: '5px',
          padding: '20px',
          textAlign: 'center',
        }}>
          <h3 style={{ color: 'var(--gold)', margin: '0 0 15px 0' }}>Need Help?</h3>
          <p style={{ color: 'var(--text-light)', margin: '0 0 15px 0', lineHeight: '1.6' }}>
            For technical support or questions about the minting process, contact BankSystem support
            or visit the MoneyTalks community forums for peer assistance.
          </p>
          <div style={{ background: 'rgba(0,255,136,0.1)', border: '1px solid #00ff88', borderRadius: '5px', padding: '12px', marginTop: '15px' }}>
            <p style={{ color: '#00ff88', margin: '0', fontSize: '14px' }}>
              <strong>Remember:</strong> Your .xbsys.eth domain is your universal key to the entire BankSystem ecosystem
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnsDomain;
